import { AspectRatio, Box, Image } from "@chakra-ui/react";

// Staggered-row geometry: all covers same source size, tilt direction
// alternates between adjacent covers (by index parity — so center shares
// its sign with the outer covers, not its neighbors), outer covers scale
// down slightly. Tuned via CoverLayoutPreview.
//
// These constants are also imported by CoverLayoutPreview as its reset-
// defaults, so keep the two in lock-step by editing here only.
export const BUNDLE_COVER_FAN_PARAMS = {
  containerAspect: 1.75,
  coverHeightPct: 63,
  xStep: 18.5,
  baseRotation: 2,
  outerRotationBump: 2,
  scaleDropPerStep: 0.045,
  // Sign of the first cover's tilt; every-other cover alternates from this.
  alternateSign: 1 as 1 | -1,
  // Drop shadow applied to every cover.
  dropShadow: "0 8px 16px rgba(0,0,0,0.4)",
} as const;

export const MAX_COVERS = 5;

const {
  containerAspect: CONTAINER_ASPECT,
  coverHeightPct: COVER_HEIGHT_PCT,
  xStep: X_STEP,
  baseRotation: BASE_ROTATION,
  outerRotationBump: OUTER_ROTATION_BUMP,
  scaleDropPerStep: SCALE_DROP_PER_STEP,
  alternateSign: ALTERNATE_SIGN,
  dropShadow: DROP_SHADOW,
} = BUNDLE_COVER_FAN_PARAMS;

export function BundleCoverFan({ covers }: { covers: string[] }) {
  const visible = covers.slice(0, MAX_COVERS);
  const count = visible.length;
  const center = (count - 1) / 2;

  return (
    <AspectRatio ratio={CONTAINER_ASPECT} width="100%">
      <Box position="relative" width="100%" height="100%" overflow="visible">
        {visible.map((url, i) => {
          const distance = i - center;
          const sign: 1 | -1 =
            i % 2 === 0 ? ALTERNATE_SIGN : ((-ALTERNATE_SIGN) as 1 | -1);
          const rotate =
            sign *
            (BASE_ROTATION + (Math.abs(distance) * OUTER_ROTATION_BUMP) / 2);
          const leftPct = 50 + distance * X_STEP;
          const scale = Math.max(
            0.4,
            1 - Math.abs(distance) * SCALE_DROP_PER_STEP,
          );

          return (
            <Box
              key={`${i}-${url}`}
              position="absolute"
              left={`${leftPct}%`}
              top="50%"
              height={`${COVER_HEIGHT_PCT}%`}
              sx={{ aspectRatio: "2 / 3" }}
              transform={`translate(-50%, -50%) rotate(${rotate}deg) scale(${scale})`}
              transformOrigin="center"
              zIndex={count - Math.abs(distance)}
              filter={`drop-shadow(${DROP_SHADOW})`}
              borderRadius="4px"
              overflow="hidden"
            >
              <Image
                src={url}
                alt=""
                width="100%"
                height="100%"
                objectFit="cover"
                draggable={false}
              />
            </Box>
          );
        })}
      </Box>
    </AspectRatio>
  );
}
